import styled from 'styled-components'
import { tokens } from 'styling/tokens'

export const HeaderControls = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
`

export const HistoricalBanner = styled.div`
  padding: 0.5rem 0.875rem;
  border-radius: ${tokens.radius.control};
  border: 1px solid ${tokens.status.info.fg};
  background: ${tokens.status.info.bg};
  color: ${tokens.status.info.fg};
  font-size: ${tokens.font.label};
  font-weight: 600;
  margin-bottom: 1rem;
`

export const StripWrap = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  margin-bottom: 1.25rem;
  border: 1px solid ${tokens.border.hairline};
  border-radius: ${tokens.radius.card};
  background: ${tokens.surface.glass};
`

export const LabelsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: none;
  width: 6.5rem;
`

export const LaneLabel = styled.span<{ $dim: boolean }>`
  height: 1.5rem;
  display: flex;
  align-items: center;
  font-size: ${tokens.font.micro};
  font-weight: 600;
  color: ${({ $dim }) => ($dim ? tokens.text.muted : tokens.text.primary)};
  white-space: nowrap;
`

export const RulerSpacer = styled.div`
  height: 1.125rem;
`

export const TracksCol = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const LaneTrack = styled.div`
  position: relative;
  height: 1.5rem;
  border-radius: ${tokens.radius.chip};
  background: ${tokens.surface.inset};
`

export const StripBar = styled.div<{ $left: number; $width: number; $dim: boolean }>`
  position: absolute;
  top: 0.125rem;
  bottom: 0.125rem;
  left: ${({ $left }) => $left}%;
  width: ${({ $width }) => $width}%;
  min-width: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${tokens.radius.chip};
  border: 1px solid ${({ $dim }) => ($dim ? tokens.border.strong : tokens.accent.base)};
  background: ${({ $dim }) => ($dim ? 'rgba(255, 255, 255, 0.05)' : tokens.accent.soft)};
  color: ${({ $dim }) => ($dim ? tokens.text.secondary : tokens.accent.base)};
  font-size: ${tokens.font.micro};
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
`

export const RulerTrack = styled.div`
  position: relative;
  height: 1.125rem;
  border-top: 1px solid ${tokens.border.hairline};
`

export const StripTickLabel = styled.span<{ $pct: number; $edge?: 'start' | 'end' }>`
  position: absolute;
  top: 0.2rem;
  left: ${({ $pct }) => $pct}%;
  transform: translateX(${({ $edge }) => ($edge === 'start' ? '0' : $edge === 'end' ? '-100%' : '-50%')});
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
`

export const NowLine = styled.div<{ $pct: number }>`
  position: absolute;
  top: -0.25rem;
  bottom: 1.125rem;
  left: ${({ $pct }) => $pct}%;
  width: 2px;
  border-radius: 1px;
  background: ${tokens.accent.base};
  box-shadow: ${tokens.accent.glow};
  z-index: 1;

  &::after {
    content: 'now';
    position: absolute;
    top: -0.125rem;
    left: 0.375rem;
    font-size: ${tokens.font.micro};
    font-weight: 700;
    color: ${tokens.accent.base};
  }
`

export const MatrixScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`

export const MatrixTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0.375rem;
`

export const MatrixHeadCell = styled.th`
  text-align: left;
  padding: 0.375rem 0.5rem;
  font-size: ${tokens.font.micro};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${tokens.text.muted};
  white-space: nowrap;
`

export const DeptCell = styled.td`
  min-width: 10rem;
  padding: 0.5rem 0.75rem;
  border-radius: ${tokens.radius.control};
  border: 1px solid ${tokens.border.hairline};
  background: ${tokens.surface.glass};
  vertical-align: middle;
  white-space: nowrap;
`

export const DeptCellName = styled.span`
  display: block;
  font-size: ${tokens.font.body};
  font-weight: 600;
  color: ${tokens.text.primary};
`

export const DeptCellSub = styled.span`
  display: block;
  margin-top: 0.25rem;
  font-size: ${tokens.font.micro};
  font-variant-numeric: tabular-nums;
  color: ${tokens.text.muted};
`

export const Cell = styled.td<{ $phase: 'active' | 'future' | 'past'; $clickable: boolean }>`
  min-width: 8.5rem;
  padding: 0.5rem 0.625rem;
  border-radius: ${tokens.radius.control};
  border: 1px ${({ $phase }) => ($phase === 'future' ? 'dashed' : 'solid')} ${tokens.border.strong};
  background: ${tokens.surface.inset};
  opacity: ${({ $phase }) => ($phase === 'past' ? 0.4 : 1)};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: border-color ${tokens.motion.duration} ${tokens.motion.ease};

  &:hover {
    border-color: ${({ $clickable }) => ($clickable ? tokens.accent.base : tokens.border.strong)};
  }
`

export const EmptyCellBox = styled.td`
  min-width: 8.5rem;
  padding: 0.5rem 0.625rem;
  text-align: center;
  color: ${tokens.text.muted};
  opacity: 0.5;
`

export const CellTop = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`

export const CellNumbers = styled.span<{ $tone: 'success' | 'warning' | 'danger' | 'neutral' }>`
  font-size: ${tokens.font.body};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone }) => ($tone === 'neutral' ? tokens.text.secondary : tokens.status[$tone].fg)};
`

export const CellDot = styled.span<{ $tone: 'success' | 'warning' | 'danger' | 'neutral' }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 100%;
  background: ${({ $tone }) => ($tone === 'neutral' ? tokens.border.strong : tokens.status[$tone].fg)};
`

export const CritBadge = styled.span`
  margin-left: auto;
  font-size: ${tokens.font.micro};
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 0.05rem 0.375rem;
  border-radius: ${tokens.radius.chip};
  color: ${tokens.status.danger.fg};
  background: ${tokens.status.danger.bg};
  border: 1px solid ${tokens.status.danger.fg};
`

export const CellSub = styled.span`
  display: block;
  margin-top: 0.25rem;
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
`

export const LegendRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.875rem;
  margin-top: 0.75rem;
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
`

export const LegendSwatch = styled.span<{ $color: string }>`
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 2px;
  margin-right: 0.25rem;
  background: ${({ $color }) => $color};
`

export const LegendDashed = styled.span`
  display: inline-block;
  width: 0.875rem;
  height: 0.625rem;
  margin-right: 0.25rem;
  border: 1px dashed ${tokens.border.strong};
  border-radius: 2px;
`

export const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999998;
  background: rgba(10, 10, 12, 0.55);
  backdrop-filter: blur(2px);
`

export const Panel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: min(460px, 94vw);
  z-index: 999999;
  background: ${tokens.surface.base};
  border-left: 1px solid ${tokens.border.strong};
  box-shadow: ${tokens.shadow.card};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

export const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid ${tokens.border.hairline};
`

export const PanelBody = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  overflow-y: auto;
  flex: 1;
`

export const PanelClose = styled.button`
  width: 2rem;
  height: 2rem;
  display: grid;
  place-items: center;
  flex: none;
  border-radius: ${tokens.radius.control};
  background: ${tokens.surface.inset};
  border: 1px solid ${tokens.border.hairline};
  color: ${tokens.text.secondary};
  cursor: pointer;

  &:hover {
    color: ${tokens.accent.base};
    border-color: ${tokens.accent.base};
  }
`

export const SectionLabel = styled.span`
  font-size: ${tokens.font.micro};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${tokens.text.muted};
`

export const BreakdownRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0;
  border-bottom: 1px solid ${tokens.border.hairline};

  &:last-child {
    border-bottom: none;
  }
`

export const BreakdownLabel = styled.span`
  font-size: ${tokens.font.label};
  color: ${tokens.text.primary};
  margin-right: auto;
`

export const BreakdownValue = styled.span<{ $tone: 'success' | 'warning' | 'danger' | 'neutral' }>`
  font-size: ${tokens.font.label};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone }) => ($tone === 'neutral' ? tokens.text.secondary : tokens.status[$tone].fg)};
`

export const PersonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0;
  border-bottom: 1px solid ${tokens.border.hairline};

  &:last-child {
    border-bottom: none;
  }
`

export const PersonName = styled.span`
  font-size: ${tokens.font.label};
  font-weight: 600;
  color: ${tokens.text.primary};
`

export const PersonRole = styled.span`
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
  margin-right: auto;
`

export const VacantRow = styled.div`
  padding: 0.375rem 0;
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};
  font-style: italic;
`

export const GapCallout = styled.div`
  padding: 0.75rem 1rem;
  border-radius: ${tokens.radius.control};
  border: 1px solid ${tokens.status.danger.fg};
  background: ${tokens.status.danger.bg};
  color: ${tokens.status.danger.fg};
  font-size: ${tokens.font.label};
  font-weight: 600;
`

export const PanelActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid ${tokens.border.hairline};

  button {
    width: 100%;
  }
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 2rem 0;
  color: ${tokens.text.muted};
`
